import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card, Spinner } from '../ui';
import { useRegister } from '../../hooks/useRegister';
import { useAuth } from '../../auth/useAuth';
import { applyScope } from '../../lib/scope';
import type { Identity, ScopeToken } from '../../lib/scope';
import { SCOPE_CONFIG } from '../../config/scopeConfig';
import { REGISTERS } from '../../config/registers';
import { SHAREPOINT_SITE_DISPLAY_URL } from '../../config/sharepoint';
import {
  daysOverdue,
  isOverdue,
  isReportable,
  riskRatingOf,
} from '../../lib/registerReaders';
import type {
  RegisterKey,
  RegisterRecord,
  RegisterStatus,
} from '../../types/qms';
import styles from './RegisterTable.module.css';

/**
 * RegisterTable (04-07) — the role-scoped, sortable, deep-linked working list
 * the office uses to work a register.
 *
 * Loads records via `useRegister` (04-02), filters them through the single scope
 * engine `applyScope` (04-03) with the supplied `viewToken` + the injected
 * `SCOPE_CONFIG` (never re-declared here, see scopeConfig.ts), then renders the
 * matrix-relevant columns: composed Reference (NEVER the calc column, ISS-008),
 * Title, Status badge, Business/Managing unit, Severity (Incidents) or Rating (Risk: residual,
 * else inherent, tagged with which), and Created. Headers are sortable; overdue rows (the
 * register's own due date, Melbourne days) carry the Harmony urgency idiom in amber
 * (⚠️ + "Nd overdue" chip — 04-CONTEXT §5, D-13-15-4, memory `user_visual_urgency_cues`);
 * each row deep-links to the SharePoint record (record-level listform, memory
 * `feedback_deep_links_in_briefs`), and its Reference opens the dashboard's record page
 * (`/record/<ref>`, 13-25), where the record is read and worked.
 *
 * Presentation-only — NO writes (authority floor §2). An honest empty state is
 * shown against today's empty registers (they populate as records are lodged).
 *
 * PALETTE: purple (`--color-primary`) stays identity-only — it is never used as a
 * filled interactive surface (04-CONTEXT §5). Urgency uses the semantic
 * warning/error tokens via the inherited `Badge`.
 *
 * An OPTIONAL `extraPredicate` lets a named view (04-07 RegisterViewsPage) layer
 * a status / reportable / rating filter ON TOP of the role scope without forking the
 * scope engine — the role's visibility is applied first, then the view narrows.
 */

// ---------------------------------------------------------------------------
// Column model
// ---------------------------------------------------------------------------

/** The columns a register table can show. `severity` is Incidents'; `rating` is Risk's. */
export type RegisterColumn =
  | 'reference'
  | 'title'
  | 'status'
  | 'businessUnit'
  | 'managingUnit'
  | 'severity'
  | 'rating'
  | 'category'
  | 'created';

/** Default column set per register (severity for Incidents, rating for Risk). */
const DEFAULT_COLUMNS: Record<RegisterKey, RegisterColumn[]> = {
  incidents: ['reference', 'title', 'status', 'managingUnit', 'severity', 'created'],
  complaints: ['reference', 'title', 'status', 'managingUnit', 'category', 'created'],
  ci: ['reference', 'title', 'status', 'managingUnit', 'category', 'created'],
  feedback: ['reference', 'title', 'status', 'businessUnit', 'category', 'created'],
  risk: ['reference', 'title', 'status', 'managingUnit', 'rating', 'created'],
};

/** Human column headers (plain CMA-language, inline — 04-CONTEXT §5). */
const COLUMN_LABELS: Record<RegisterColumn, string> = {
  reference: 'Reference',
  title: 'Title',
  status: 'Status',
  businessUnit: 'Business unit',
  managingUnit: 'Managing unit',
  severity: 'Severity',
  rating: 'Rating',
  category: 'Category',
  created: 'Created',
};

// ---------------------------------------------------------------------------
// Status → Badge tone (the inherited semantic vocabulary)
// ---------------------------------------------------------------------------

function statusTone(
  status: RegisterStatus,
): 'success' | 'warning' | 'info' | 'neutral' {
  switch (status) {
    case 'Completed':
      return 'success';
    case 'Actioning':
    case 'Evaluating':
      return 'info';
    case 'Cancelled':
      return 'neutral';
    case 'New':
    default:
      return 'warning';
  }
}

// Overdue / days overdue / rating / reportable come from `lib/registerReaders.ts` (13-16), the
// same readers the tiles use, so a row and its tile never disagree.

function categoryOf(record: RegisterRecord): string | null {
  return (record as { category?: string | null }).category ?? null;
}

/** ISO → friendly short date (en-AU, day-month-year). Blank-safe. */
function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  return new Date(t).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Build the record-level deep link to the SharePoint list display form
 * (`…/Lists/<ListTitle>/DispForm.aspx?ID=<id>`). Record-level, not the landing
 * page (memory `feedback_deep_links_in_briefs`). The list title comes from the
 * single REGISTERS source — never hard-coded.
 */
function recordUrl(registerKey: RegisterKey, id: number): string {
  const listTitle = REGISTERS[registerKey].listTitle;
  return `${SHAREPOINT_SITE_DISPLAY_URL}/Lists/${encodeURIComponent(
    listTitle,
  )}/DispForm.aspx?ID=${id}`;
}

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

type SortDir = 'asc' | 'desc';

/** Read the comparable primitive for a column (string-ish, lowercased). */
function sortValue(record: RegisterRecord, column: RegisterColumn): string {
  switch (column) {
    case 'reference':
      // Sort references numerically by the trailing id (INC-2 before INC-10).
      return String(record.id).padStart(12, '0');
    case 'title':
      return (record.title ?? '').toLowerCase();
    case 'status':
      return record.status ?? '';
    case 'businessUnit':
      return (record.businessUnit ?? '').toLowerCase();
    case 'managingUnit':
      return (record.managingUnit ?? '').toLowerCase();
    case 'severity':
      return (record.severity ?? '').toLowerCase();
    case 'rating':
      return (riskRatingOf(record)?.rating ?? '').toLowerCase();
    case 'category':
      return (categoryOf(record) ?? '').toLowerCase();
    case 'created':
      // Compare by epoch so date sorting is chronological, not lexical.
      return String(Date.parse(record.created) || 0).padStart(16, '0');
    default:
      return '';
  }
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface RegisterTableProps {
  /** Which register to load + render. */
  registerKey: RegisterKey;
  /**
   * The scope token applied via `applyScope`. Pass the role's default scope
   * (`defaultScopeFor`) for the role view, or force a token for a named view
   * (e.g. `'Own only'` for My-Incidents).
   */
  viewToken: ScopeToken;
  /** Columns to render (defaults to the register's sensible column set). */
  columns?: RegisterColumn[];
  /**
   * OPTIONAL extra predicate layered ON TOP of the role scope — realizes a named
   * view (Open-Overdue / SIRS / By-Rating / Compliments) without forking the
   * scope engine. Role visibility is applied first, then this narrows further.
   */
  extraPredicate?: (record: RegisterRecord) => boolean;
  /** Empty-state line, tailored per named view (else a sensible default). */
  emptyMessage?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function RegisterTable({
  registerKey,
  viewToken,
  columns,
  extraPredicate,
  emptyMessage,
}: RegisterTableProps) {
  const { user } = useAuth();
  const { data, isLoading, error } = useRegister(registerKey);

  // A stable per-mount "now" for the overdue cut (React-pure lazy init — same
  // idiom as useRegisterMetrics; not Date.now() inside render).
  const [now] = useState(() => Date.now());

  // Sort state — default newest first (Created desc) so the freshest work is up.
  const [sortColumn, setSortColumn] = useState<RegisterColumn>('created');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Scope identity (04-03). businessUnits EMPTY → Portfolio grouped fallback
  // (widens nothing; no per-CM BU map yet, 04-CONTEXT §8).
  const identity: Identity = useMemo(
    () => ({ upn: user?.email ?? '', businessUnits: [] }),
    [user?.email],
  );

  const cols = columns ?? DEFAULT_COLUMNS[registerKey];

  const rows = useMemo(() => {
    // SCOPE FIRST (04-03) — every row reflects the role's visible slice.
    const scoped = applyScope(viewToken, identity, registerKey, data, SCOPE_CONFIG);
    // Then narrow by the named-view predicate (if any).
    const narrowed = extraPredicate ? scoped.filter(extraPredicate) : scoped;
    // Then sort.
    const sorted = [...narrowed].sort((a, b) => {
      const av = sortValue(a, sortColumn);
      const bv = sortValue(b, sortColumn);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [data, identity, viewToken, registerKey, extraPredicate, sortColumn, sortDir]);

  const toggleSort = (column: RegisterColumn) => {
    if (column === sortColumn) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDir(column === 'created' ? 'desc' : 'asc');
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loading} role="status" aria-live="polite">
        <Spinner size="lg" />
        <span className={styles.loadingText}>Loading records…</span>
      </div>
    );
  }

  if (error) {
    return (
      <p role="alert" className={styles.error}>
        Couldn’t load this register from the Quality &amp; Safety site: {error}
      </p>
    );
  }

  if (rows.length === 0) {
    return (
      <Card variant="outlined" padding="lg" className={styles.empty}>
        <p className={styles.emptyTitle}>Nothing to show here yet</p>
        <p className={styles.emptyBody}>
          {emptyMessage ??
            'No records match this view yet. They’ll appear here as items are lodged and worked.'}
        </p>
      </Card>
    );
  }

  return (
    <Card variant="elevated" padding="none" className={styles.tableCard}>
      <div className={styles.scroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              {cols.map((col) => {
                const active = col === sortColumn;
                return (
                  <th key={col} scope="col" className={styles.th}>
                    <button
                      type="button"
                      className={styles.sortBtn}
                      onClick={() => toggleSort(col)}
                      aria-sort={
                        active
                          ? sortDir === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                    >
                      {COLUMN_LABELS[col]}
                      <span className={styles.sortArrow} aria-hidden="true">
                        {active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
                      </span>
                    </button>
                  </th>
                );
              })}
              <th scope="col" className={styles.th} aria-label="Open in SharePoint" />
            </tr>
          </thead>
          <tbody>
            {rows.map((record) => {
              const overdue = isOverdue(record, now);
              const nDays = overdue ? daysOverdue(record, now) : 0;
              return (
                <tr
                  key={record.id}
                  className={overdue ? styles.rowOverdue : undefined}
                >
                  {cols.map((col) => (
                    <td key={col} className={styles.td}>
                      {renderCell(record, col, overdue, nDays)}
                    </td>
                  ))}
                  <td className={styles.tdAction}>
                    <a
                      className={styles.openLink}
                      href={recordUrl(registerKey, record.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Open ${record.reference} in SharePoint`}
                    >
                      Open ↗
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Cell rendering
// ---------------------------------------------------------------------------

function renderCell(
  record: RegisterRecord,
  column: RegisterColumn,
  overdue: boolean,
  nDays: number,
) {
  switch (column) {
    case 'reference':
      // Composed <PREFIX>-<Id> (ISS-008) — never the calc Reference column. Opens the record page (13-25).
      return (
        <Link className={styles.reference} to={`/record/${record.reference}`} aria-label={`Open ${record.reference}`}>
          {record.reference}
        </Link>
      );

    case 'title':
      return (
        <span className={styles.titleCell}>
          <span className={styles.titleText}>{record.title || '—'}</span>
          {/* Reportable flag inline on Incidents (ReportabilityDecision ∈ SIRS-P1 / SIRS-P2 /
              NDIS-Reportable / WorkSafe) so the cut reads at a glance (04-CONTEXT §5). */}
          {isReportable(record) && (
            <Badge variant="warning" size="sm">
              Reportable
            </Badge>
          )}
        </span>
      );

    case 'status':
      return (
        <span className={styles.statusCell}>
          <Badge variant={statusTone(record.status)} size="sm">
            {record.status}
          </Badge>
          {overdue && (
            <Badge variant="warning" size="sm">
              ⚠️ {nDays}d overdue
            </Badge>
          )}
        </span>
      );

    case 'businessUnit':
      return <span className={styles.cellText}>{record.businessUnit ?? '—'}</span>;

    case 'managingUnit':
      return (
        <span className={styles.cellText}>
          {record.managingUnit ?? record.businessUnit ?? '—'}
        </span>
      );

    case 'severity':
      return <span className={styles.cellText}>{record.severity ?? '—'}</span>;

    case 'rating': {
      const reading = riskRatingOf(record);
      if (!reading) return <span className={styles.cellText}>—</span>;
      return (
        <span className={styles.cellText}>
          {reading.rating} <span className={styles.ratingBasis}>{reading.basis}</span>
        </span>
      );
    }

    case 'category':
      return <span className={styles.cellText}>{categoryOf(record) ?? '—'}</span>;

    case 'created':
      return <span className={styles.cellText}>{formatDate(record.created)}</span>;

    default:
      return null;
  }
}
